import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { api } from '../../lib/api';

// AccountNotificationRouting — Sprint D item 3.8 admin-side panel embedded
// on AccountDetail's Notifications sub-tab. Per-event dropdown of school
// users (defaults to the principal). Saved via PATCH.

const EVENT_LABELS = {
  edit_request_resolved:    'Edit request resolved',
  ticket_resolved:          'Ticket resolved',
  invoice_issued:           'Invoice issued',
  invoice_signed:           'Invoice signed',
  payment_received:         'Payment received',
  maintenance_renewal_due:  'Maintenance renewal due',
  audit_completed:          'Audit completed',
  // Sprint I D12: 8th event — outreach reply detection (mig 031 CHECK extension).
  outreach_reply_received:  'Outreach reply received',
};

export default function AccountNotificationRouting({ accountId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    api.get(`/api/admin/accounts/${accountId}/notification-routing`)
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(err?.data?.error || err?.message || 'Failed to load'); });
    return () => { alive = false; };
  }, [accountId]);

  const onChange = async (eventType, userId) => {
    setSaving(eventType);
    try {
      await api.patch(`/api/admin/accounts/${accountId}/notification-routing`, {
        routes: { [eventType]: userId || null },
      });
      setData((prev) => prev ? { ...prev, routes: { ...prev.routes, [eventType]: userId || null } } : prev);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  if (!data && !error) return <div className="text-[12px] text-gray-500">Loading…</div>;
  if (error) return <div className="text-[12px] text-red-300/90">{error}</div>;

  return (
    <div data-testid="account-notification-routing" className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-3">
        <Bell className="w-3 h-3" />
        Notification routing
      </div>
      <p className="text-[12px] text-gray-400 mb-4">
        Override which user at the school receives each notification. Default is the primary client user.
      </p>
      <table className="w-full text-[13px]">
        <thead className="text-gray-500 uppercase tracking-wider text-[10px]">
          <tr><th className="text-left py-1.5">Event</th><th className="text-left py-1.5">Routed to</th></tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {data.events.map((e) => (
            <tr key={e} data-testid={`routing-row-${e}`}>
              <td className="py-2.5 text-gray-300">{EVENT_LABELS[e] || e}</td>
              <td className="py-2.5">
                <select
                  data-testid={`routing-select-${e}`}
                  className="px-2 py-1 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[12px] text-white"
                  value={data.routes[e] || ''}
                  disabled={saving === e}
                  onChange={(ev) => onChange(e, ev.target.value)}
                >
                  <option value="">Default (primary client)</option>
                  {data.candidates.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
